import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip, FormControlLabel, Icon, Stack, Typography } from '@mui/material';
import { booleanHeader, CreateHeaders, dateFormatter, dateHeader, textHeader } from '@frontend-workspace/react-shared-lib';
import { useTranslation } from 'react-i18next';
import WebkuaTable from '../webkua-table/webkua-table';
import {
  ReconciliationLink, ReconciliationLinks, salesforceRecordUrl, webkuaContractUrl, webkuaProductListUrl,
} from './reconciliation-links';
import {
  AssetContractLinks, AssetDiagnosticBadges, AssetItemCodes, AssetQuantityLink, AssetReconciliationDetails, AssetRenewalDates,
  assetQuickFilters, assetStatus, matchesAssetFilter, salesforceUsageDates,
} from './asset-reconciliation-worktable';
import type { AssetQuickFilter } from './asset-reconciliation-worktable';
import { ReconciliationComparisonFilters } from './reconciliation-filter-select';
import { useAssetSyncControls } from './asset-sync-controls';
import type { AssetSyncMode } from './reconciliation-sync-controls';

export interface PendingAssetOrder {
  id: string;
  companyNumber: string | null;
  contractNumber: string | null;
  salesforceContractNumber: string | null;
  itemId: string | null;
  quantity: number;
  activationDate: string;
  orderDate?: string | null;
}

export interface SalesforceAssetDetail {
  id: string | null;
  companyNumber: string | null;
  contractNumber: string | null;
  contractId: string | null;
  name: string | null;
  productCode: string | null;
  status: string | null;
  statusReason: string | null;
  quantity: number | null;
  activeQuantity: number | null;
  renewalQuantity: number | null;
  discountPercent: number | null;
  usageEndDate: string | null;
  cancellationDate: string | null;
  cancellationReason: string | null;
  excludeFromRenewal: boolean | null;
  renewalExclusionReason: string | null;
  accountId?: string | null;
  recurrence?: string | null;
  isSupport?: boolean;
  validationFieldsComplete?: boolean;
  lifecycleFieldsComplete?: boolean;
  installDate?: string | null;
}

export interface WebkuaAssetDetail {
  licenseId: string;
  licenseNumber: number;
  companyNumber: string | null;
  contractNumber: string | null;
  itemLineId: string | null;
  itemId: string | null;
  quantity: number;
  activeQuantity: number;
  isActive: boolean;
  isOneTime: boolean;
  licenseIsActive: boolean | null;
  itemIsActive: boolean | null;
  discountPercent: number | null;
  licenseTerminationDate: string | null;
  maintenanceExpirationDate: string | null;
  itemTerminationDate: string | null;
  lastUsedDate2026: string | null;
  nextInvoicingDate: string | null;
  usedAfterRenewalDate: boolean | null;
}

export interface AssetReconciliationResult {
  comparedAt?: string | null;
  salesforceAssets?: SalesforceAssetDetail[];
  isComplete: boolean;
  incompleteReason: string | null;
  incompleteReasonCode?: string | null;
  comparedGroupCount: number;
  inSyncGroupCount: number;
  pendingOrders?: PendingAssetOrder[];
  rows: Array<{
    syncBlockReason?: string | null;
    syncMode?: AssetSyncMode | null;
    syncLicenseId?: string | null;
    syncItemLineId?: string | null;
    companyNumber: string | null;
    companyName?: string | null;
    itemName?: string | null;
    salesforceContractNumber: string | null;
    itemId: string | null;
    salesforceActiveQuantity: number | null;
    webkuaActiveQuantity: number | null;
    kind: string;
    salesforceAssetIds: string[];
    webkuaLicenseNumbers: number[];
    salesforceDiscountPercents?: Array<number | null>;
    otherWebkuaContracts?: Array<{
      contractNumber: string;
      activeQuantity: number;
      licenseNumbers: number[];
    }>;
    duplicateWebkuaLicenseNumbers?: number[];
    pendingOrders?: PendingAssetOrder[];
    latestPendingOrder?: PendingAssetOrder | null;
    salesforceAssets?: SalesforceAssetDetail[];
    webkuaItems?: WebkuaAssetDetail[];
    comparisonIssues?: string[];
    productMatchIssue?: string | null;
    productCandidates?: Array<{
      itemId: string;
      itemName: string | null;
      itemType: string;
      webkuaActiveQuantity: number | null;
      webkuaLicenseNumbers: number[];
      webkuaItems: WebkuaAssetDetail[];
    }>;
    discountDecimalPlaces?: number;
    discountGroups?: Array<{
      discountPercent: number | null;
      salesforceQuantity: number | null;
      webkuaQuantity: number | null;
    }>;
    companyItemComparison?: {
      status: string;
      quantityDifference: number | null;
      discountDifferences: Array<{ discountPercent: number; quantityDifference: number }>;
      contracts: Array<{
        salesforceContractNumber: string | null;
        salesforceContractIds: string[];
        webkuaContractNumbers: string[];
        salesforceActiveQuantity: number | null;
        webkuaActiveQuantity: number | null;
        quantityDifference: number | null;
        kind: string;
        discountGroups: Array<{
          discountPercent: number | null;
          salesforceQuantity: number | null;
          webkuaQuantity: number | null;
        }>;
      }>;
    } | null;
    webkuaContractIds?: string[];
    salesforceContractIds?: string[];
    salesforceAccountIds?: string[];
    lastUsedDate2026?: string | null;
    usedAfterRenewalDate?: boolean | null;
    webkuaContractNumber?: string | null;
    webkuaContractNumbers?: string[];
    salesforceProductCode?: string | null;
    candidateSalesforceContractNumbers?: string[];
    webkuaContractDates?: Array<{
      contractNumber: string | null;
      nextInvoicingDate: string | null;
    }>;
    webkuaLicenseDates?: Array<{
      licenseNumber: number;
      licenseTerminationDate: string | null;
      maintenanceExpirationDate: string | null;
      itemTerminationDate: string | null;
    }>;
  }>;
}

export function SalesForceAssetReconciliation({ result, onRefresh }: {
  result: AssetReconciliationResult; onRefresh?: () => void;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<AssetQuickFilter | ''>('');
  const [onlySyncable, setOnlySyncable] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const sync = useAssetSyncControls(onRefresh);
  const now = new Date();
  const formatDates = (
    dates: NonNullable<AssetReconciliationResult['rows'][number]['webkuaLicenseDates']>,
    field: 'licenseTerminationDate' | 'maintenanceExpirationDate' | 'itemTerminationDate',
  ) => dates.map((item) => `${item.licenseNumber}: ${item[field] ?? '-'}`).join(', ');
  const formatContractDates = (
    dates: NonNullable<AssetReconciliationResult['rows'][number]['webkuaContractDates']>,
  ) => dates.length === 1
    ? dates[0].nextInvoicingDate?.slice(0, 10) ?? '-'
    : dates.map((contract) =>
      `${contract.contractNumber ?? '-'}: ${contract.nextInvoicingDate?.slice(0, 10) ?? '-'}`).join(', ');
  const rows = result.rows.map((row) => {
    const latest = row.latestPendingOrder ??
      (row.pendingOrders?.length === 1 ? row.pendingOrders[0] : null);
    const hasPendingOrders = (row.pendingOrders?.length ?? 0) > 0;
    return {
      ...row,
      source: row,
      contractSummary: `SF: ${row.salesforceContractNumber ?? '-'} / WK: ${row.webkuaContractNumbers?.join(', ') || row.webkuaContractNumber || '-'}`,
      statusSummary: assetStatus(row, t),
      companyItemStatus: row.companyItemComparison
        ? t(`salesForceAssetReconciliation.companyComparison.${row.companyItemComparison.status}`) : '',
      companyItemDifference: row.companyItemComparison?.quantityDifference ?? null,
      companyItemDiscountDifferences: row.companyItemComparison?.discountDifferences
        .map((band) => `${band.discountPercent}%: ${band.quantityDifference}`).join('; ') ?? '',
      productMatchIssue: row.productMatchIssue
        ? t(`salesForceAssetReconciliation.${row.productMatchIssue}`) : '',
      productCandidatesSummary: row.productCandidates?.map((candidate) =>
        `${candidate.itemId} (${candidate.itemType}): WK ${candidate.webkuaActiveQuantity ?? '-'}; ${candidate.webkuaLicenseNumbers.join(', ')}`)
        .join(' | ') ?? '',
      webkuaContractNumber: row.webkuaContractNumbers?.length
        ? row.webkuaContractNumbers.join(', ')
        : row.webkuaContractNumber ?? '',
      kind: t(`salesForceAssetReconciliation.${row.kind}`, { defaultValue: row.kind }),
      salesforceAssetIds: row.salesforceAssetIds.join(', '),
      salesforceDiscountPercents: [...new Set(row.salesforceDiscountPercents
        ?.map((discount) => String(discount ?? 0)) ?? [])].join(', '),
      discountGroupSummary: row.discountGroups?.map((group) => t('salesForceAssetReconciliation.discountGroupSummary', {
        discount: group.discountPercent == null ? t('salesForceAssetReconciliation.unknownDiscount') : `${group.discountPercent}%`,
        sfQuantity: group.salesforceQuantity ?? '-', wkQuantity: group.webkuaQuantity ?? '-',
      })).join('; ') ?? '',
      otherWebkuaContractItems: row.kind === 'MissingInWebkua' ? row.otherWebkuaContracts ?? [] : [],
      otherWebkuaContracts: row.kind === 'MissingInWebkua'
        ? row.otherWebkuaContracts?.map((contract) =>
          t('salesForceAssetReconciliation.otherContractItem', {
            contractNumber: contract.contractNumber,
            quantity: contract.activeQuantity,
            licenseNumbers: contract.licenseNumbers.join(', '),
          })).join('; ') ?? ''
        : '',
      webkuaLicenseNumbers: row.webkuaLicenseNumbers.join(', '),
      duplicateWebkuaLicenseNumbers: row.duplicateWebkuaLicenseNumbers?.join(', ') ?? '',
      hasPendingOrders,
      latestPendingQuantity: latest?.quantity ?? null,
      latestPendingActivationDate: latest?.activationDate ?? null,
      pendingOrderSummaryUnresolved: hasPendingOrders && latest === null,
      webkuaContractIds: row.webkuaContractIds?.join(', ') ?? '',
      candidateSalesforceContractNumbers: row.candidateSalesforceContractNumbers?.join(', ') ?? '',
      webkuaContractRenewalDates: formatContractDates(row.webkuaContractDates ?? []),
      salesforceUsageEndDates: salesforceUsageDates(row),
      renewalSummary: `SF: ${salesforceUsageDates(row)} / WK: ${formatContractDates(row.webkuaContractDates ?? []) || '-'}`,
      webkuaLicenseTerminationDates: formatDates(row.webkuaLicenseDates ?? [], 'licenseTerminationDate'),
      webkuaMaintenanceExpirationDates: formatDates(row.webkuaLicenseDates ?? [], 'maintenanceExpirationDate'),
      webkuaItemTerminationDates: formatDates(row.webkuaLicenseDates ?? [], 'itemTerminationDate'),
    };
  });

  type Row = typeof rows[number];
  const visibleFields = [
    'companyNumber', 'contractSummary', 'itemId', 'salesforceActiveQuantity', 'webkuaActiveQuantity',
    'statusSummary', 'renewalSummary', 'lastUsedDate2026', 'hasPendingOrders', 'syncAction',
  ];
  const displayedRows = rows.filter((row) => (!onlySyncable || sync.canSync(row.source)) &&
    (!filter || matchesAssetFilter(row.source, filter, now)));
  const contractRenderer = ({ data }: { data?: Row }) => {
    if (!data?.salesforceContractNumber) return null;
    const ids = data.salesforceContractIds ?? [];
    return ids.length
      ? <ReconciliationLinks links={ids.map((id) => ({
        label: data.salesforceContractNumber ?? id, href: salesforceRecordUrl('Contract', id),
      }))} />
      : <>{data.salesforceContractNumber}</>;
  };
  const webkuaContractRenderer = ({ data }: { data?: Row }) => {
    if (!data) return null;
    const numbers = data.source.webkuaContractNumbers?.length ? data.source.webkuaContractNumbers
      : data.source.webkuaContractNumber ? [data.source.webkuaContractNumber] : [];
    return <ReconciliationLinks links={numbers.filter(Boolean).map((number) => ({
      label: number, href: data.companyNumber ? webkuaContractUrl(data.companyNumber, number) : null,
    }))} />;
  };
  const assetRenderer = ({ data }: { data?: Row }) => data
    ? <ReconciliationLinks links={data.source.salesforceAssetIds.filter(Boolean).map((id) => ({
      label: id, href: salesforceRecordUrl('Asset', id),
    }))} />
    : null;
  const licenseRenderer = (field: 'webkuaLicenseNumbers' | 'duplicateWebkuaLicenseNumbers') =>
    ({ data }: { data?: Row }) => data?.[field]
      ? <ReconciliationLink label={data[field]}
        href={data[field] && data.companyNumber ? webkuaProductListUrl(data.companyNumber) : null} />
      : null;

  return (
    <Box>
      <Alert severity="info">
        {result.comparedGroupCount > 0
          ? t('salesForceAssetReconciliation.inSyncGroups', {
            inSync: result.inSyncGroupCount,
            compared: result.comparedGroupCount,
          })
          : t('salesForceAssetReconciliation.noComparableGroups')}
      </Alert>
      {rows.some((row) => row.pendingOrderSummaryUnresolved) && (
        <Alert severity="warning">
          {t('salesForceAssetReconciliation.latestPendingUnresolved')}{' '}
          {rows.filter((row) => row.pendingOrderSummaryUnresolved).map((row) =>
            `${row.companyNumber ?? '-'} / ${row.webkuaContractNumber || row.salesforceContractNumber || '-'} / ${row.itemId ?? '-'}`)
            .join('; ')}
        </Alert>
      )}
      {result.isComplete && rows.length === 0 && (
        <Alert severity="success">{t('salesForceAccountReconciliation.noDifferences')}</Alert>
      )}
      <ReconciliationComparisonFilters rows={result.rows} filters={assetQuickFilters}
        filter={filter} onFilterChange={setFilter} onlySyncable={onlySyncable} onSyncableChange={setOnlySyncable}
        matches={(row, value) => matchesAssetFilter(row, value, now)} canSync={sync.canSync}
        filterLabel={(value) => t(`salesForceAssetReconciliation.filters.${value}`)} />
      <WebkuaTable
        data={displayedRows}
        customComponent={<Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography variant="body2">{t('salesForceAssetReconciliation.filteredRows', {
            shown: displayedRows.length, total: rows.length,
          })}</Typography>
          <Button size="small" onClick={() => { setFilter(''); setOnlySyncable(false); }}
            disabled={!filter && !onlySyncable}>
            {t('salesForceAssetReconciliation.clearFilters')}
          </Button>
          <FormControlLabel control={<Checkbox checked={showDiscount}
            onChange={(_, checked) => setShowDiscount(checked)} />}
          label={t('salesForceAssetReconciliation.showDiscount')} />
        </Stack>}
        expandComponent={AssetReconciliationDetails}
        exportColumnKeys={[
          'companyNumber', 'companyName', 'salesforceContractNumber', 'webkuaContractNumber',
          'webkuaContractRenewalDates', 'salesforceUsageEndDates', 'candidateSalesforceContractNumbers', 'itemId', 'itemName',
          'salesforceProductCode', 'salesforceActiveQuantity', 'webkuaActiveQuantity', 'lastUsedDate2026',
          'usedAfterRenewalDate', 'salesforceDiscountPercents', 'discountGroupSummary', 'kind', 'otherWebkuaContracts',
          'companyItemStatus', 'companyItemDifference', 'companyItemDiscountDifferences',
          'productMatchIssue', 'productCandidatesSummary',
          'hasPendingOrders', 'latestPendingQuantity', 'latestPendingActivationDate',
          'salesforceAssetIds', 'webkuaLicenseNumbers', 'duplicateWebkuaLicenseNumbers',
          'webkuaLicenseTerminationDates', 'webkuaMaintenanceExpirationDates',
          'webkuaItemTerminationDates', 'webkuaContractIds',
        ]}
        headers={CreateHeaders([
          textHeader('contractSummary', t('salesForceAssetReconciliation.contracts'), undefined, {
            renderer: ({ data }: { data?: Row }) => data?.source ? <AssetContractLinks row={data.source} /> : null,
            getQuickFilterText: ({ data }: { data?: Row }) =>
              [data?.contractSummary, ...(data?.source.salesforceContractIds ?? []),
                ...(data?.source.webkuaContractIds ?? [])].join(' '),
          }),
          textHeader('statusSummary', t('salesForceAccountReconciliation.mainFinding'), undefined, {
            renderer: ({ data }: { data?: Row }) => data?.source ? <Stack spacing={0.25}>
              <span>{data.statusSummary}</span><AssetDiagnosticBadges row={data.source} />
            </Stack> : null,
            getQuickFilterText: ({ data }: { data?: Row }) =>
              [data?.statusSummary, data?.kind, data?.companyItemStatus, data?.productMatchIssue,
                data?.otherWebkuaContracts ? t('salesForceAssetReconciliation.otherContractBadge') : ''].join(' '),
          }),
          textHeader('renewalSummary', t('salesForceAssetReconciliation.renewal'), undefined, {
            renderer: ({ data }: { data?: Row }) => data?.source ? <AssetRenewalDates row={data.source} /> : null,
          }),
          textHeader('companyName', t('standard.companyName')),
          textHeader('itemName', t('standard.productName')),
          textHeader('companyNumber', t('standard.companyNumber'), undefined, {
            getQuickFilterText: ({ data }: { data?: Row }) =>
              [data?.companyNumber, data?.companyName, ...(data?.source.salesforceAccountIds ?? [])].join(' '),
            renderer: ({ data }: { data?: Row }) => {
              if (!data?.source) return null;
              const ids = data.salesforceAccountIds ?? [];
              const label = data.companyName
                ? data.companyNumber ? `${data.companyName} (${data.companyNumber})` : data.companyName
                : data.companyNumber ?? '-';
              return <Box sx={{ whiteSpace: 'nowrap' }} title={label}>{ids.length
                ? <ReconciliationLinks links={ids.map((id) => ({
                  label, href: salesforceRecordUrl('Account', id),
                }))} />
                : label}
              </Box>;
            },
          }),
          textHeader('salesforceContractNumber', t('salesForceAssetReconciliation.contractNumber'), undefined, {
            renderer: contractRenderer,
          }),
          textHeader('webkuaContractNumber', t('salesForceAssetReconciliation.webkuaContractNumber'), undefined, {
            renderer: webkuaContractRenderer,
          }),
          textHeader('webkuaContractRenewalDates', t('salesForceAssetReconciliation.webkuaContractRenewalDates')),
          textHeader('salesforceUsageEndDates', t('salesForceAssetReconciliation.salesforceUsageEndDates')),
          textHeader('candidateSalesforceContractNumbers', t('salesForceAssetReconciliation.candidateContracts')),
          textHeader('itemId', t('salesForceAssetReconciliation.item'), undefined, {
            renderer: ({ data }: { data?: Row }) => data?.source ? <AssetItemCodes row={data.source} /> : null,
            getQuickFilterText: ({ data }: { data?: Row }) =>
              [data?.itemId, data?.itemName, data?.salesforceProductCode].filter(Boolean).join(' '),
          }),
          textHeader('salesforceProductCode', t('salesForceAssetReconciliation.productCode')),
          textHeader('salesforceActiveQuantity', t('salesForceAssetReconciliation.sfQuantity'), undefined, {
            cellStyle: { justifyContent: 'center' },
            renderer: ({ data }: { data?: Row }) => data?.source ? <AssetQuantityLink row={data.source} system="SF" /> : null,
            getQuickFilterText: ({ data }: { data?: Row }) =>
              [data?.salesforceActiveQuantity, data?.salesforceAssetIds].filter((value) => value != null).join(' '),
          }),
          textHeader('webkuaActiveQuantity', t('salesForceAssetReconciliation.wkQuantity'), undefined, {
            cellStyle: { justifyContent: 'center' },
            renderer: ({ data }: { data?: Row }) => data?.source ? <AssetQuantityLink row={data.source} system="WK" /> : null,
            getQuickFilterText: ({ data }: { data?: Row }) =>
              [data?.webkuaActiveQuantity, data?.webkuaLicenseNumbers].filter((value) => value != null).join(' '),
          }),
          dateHeader('lastUsedDate2026', t('salesForceAssetReconciliation.usage'), {
            renderer: ({ data }: { data?: Row }) => data?.source ? <Box sx={{ lineHeight: 1.35 }}>
              <span>{dateFormatter(data.lastUsedDate2026, true)}</span>
              {data.usedAfterRenewalDate === true && <div><Chip size="small" color="warning"
                label={t('salesForceAssetReconciliation.afterRenewalBadge')} /></div>}
            </Box> : null,
          }),
          booleanHeader('usedAfterRenewalDate', t('salesForceAssetReconciliation.usedAfterRenewalDate'), {
            renderer: ({ data, value }: { data?: { isSummary?: boolean }; value?: boolean | null }) =>
              data?.isSummary ? null : value == null ? '-' : <Checkbox disabled checked={value} />,
          }),
          textHeader('salesforceDiscountPercents', t('salesForceAssetReconciliation.salesforceDiscount')),
          textHeader('discountGroupSummary', t('salesForceAssetReconciliation.discountAllocation')),
          textHeader('companyItemStatus', t('salesForceAssetReconciliation.companyComparisonTitle')),
          textHeader('companyItemDifference', t('salesForceAssetReconciliation.netDifference')),
          textHeader('companyItemDiscountDifferences', t('salesForceAssetReconciliation.netDiscountDifferences')),
          textHeader('productMatchIssue', t('salesForceAssetReconciliation.productMatchIssue')),
          textHeader('productCandidatesSummary', t('salesForceAssetReconciliation.productCandidates')),
          textHeader('kind', t('salesForceAccountReconciliation.status')),
          textHeader('otherWebkuaContracts', t('salesForceAssetReconciliation.otherWebkuaContracts'), undefined, {
              renderer: ({ data }: { data?: Row }) => data?.otherWebkuaContractItems?.length
                ? <ReconciliationLinks links={data.otherWebkuaContractItems.map((contract) => ({
                  label: t('salesForceAssetReconciliation.otherContractItem', {
                    contractNumber: contract.contractNumber, quantity: contract.activeQuantity,
                    licenseNumbers: contract.licenseNumbers.join(', '),
                  }),
                  href: data.companyNumber ? webkuaContractUrl(data.companyNumber, contract.contractNumber) : null,
                }))} />
                : null,
            }),
          booleanHeader('hasPendingOrders', t('salesForceAssetReconciliation.sync'), {
            renderer: ({ data }: { data?: Row }) => data ? <Checkbox disabled checked={data.hasPendingOrders}
              inputProps={{ 'aria-label': String(t('salesForceAssetReconciliation.hasPendingOrders')) }} /> : null,
          }),
          textHeader('latestPendingQuantity', t('salesForceAssetReconciliation.latestPendingQuantity')),
          textHeader('latestPendingActivationDate', t('salesForceAssetReconciliation.latestPendingActivationDate')),
          textHeader('salesforceAssetIds', t('salesForceAssetReconciliation.salesforceAssetIds'), undefined, {
            renderer: assetRenderer,
          }),
          textHeader('webkuaLicenseNumbers', t('salesForceAssetReconciliation.webkuaLicenseNumbers'), undefined, {
            renderer: licenseRenderer('webkuaLicenseNumbers'),
          }),
          textHeader('duplicateWebkuaLicenseNumbers', t('salesForceAssetReconciliation.duplicateWebkuaLicenseNumbers'), undefined, {
            renderer: licenseRenderer('duplicateWebkuaLicenseNumbers'),
          }),
          textHeader('webkuaLicenseTerminationDates', t('salesForceAssetReconciliation.licenseTerminationDates')),
          textHeader('webkuaMaintenanceExpirationDates', t('salesForceAssetReconciliation.maintenanceExpirationDates')),
          textHeader('webkuaItemTerminationDates', t('salesForceAssetReconciliation.itemTerminationDates')),
          textHeader('webkuaContractIds', t('salesForceAssetReconciliation.webkuaContractIds')),
          textHeader('syncAction', t('salesForceAssetReconciliationSync.sync.column'), undefined, {
            renderer: ({ data }: { data?: Row }) => data ? sync.action(data.source) : null,
          }),
        ]).map((header) => ({
          ...header,
          hide: !visibleFields.includes(header.field) &&
            !(showDiscount && header.field === 'salesforceDiscountPercents'),
          ...(header.field === 'companyNumber' ? {
            cellRenderer: 'agGroupCellRenderer', cellRendererParams: { innerRenderer: header.cellRenderer },
          } : {}),
          ...(['salesforceActiveQuantity', 'webkuaActiveQuantity'].includes(header.field)
            ? { width: 95, minWidth: 90, flex: 0, filter: 'agNumberColumnFilter' } : {}),
          ...(header.field === 'companyNumber' ? { width: 180, minWidth: 160 } : {}),
          ...(header.field === 'renewalSummary' ? { width: 160, minWidth: 140 } : {}),
          ...(header.field === 'syncAction' ? { pinned: 'right', lockPinned: true, suppressMovable: true,
            width: 260, minWidth: 240, flex: 0, suppressSizeToFit: true,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' } } : {}),
        })).sort((left, right) => {
          if (left.field === 'syncAction') return 1;
          if (right.field === 'syncAction') return -1;
          const a = visibleFields.indexOf(left.field);
          const b = visibleFields.indexOf(right.field);
          return (a < 0 ? visibleFields.length : a) - (b < 0 ? visibleFields.length : b);
        })}
      />
      <Accordion disableGutters elevation={0} sx={{ mt: 2 }} TransitionProps={{ unmountOnExit: true }}>
        <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
          <Typography variant="body2">{t('salesForceAssetReconciliation.aboutComparison')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            <Typography variant="body2">{t('salesForceAssetReconciliation.quantityExplanation')}</Typography>
            <Typography variant="body2">{t('salesForceAssetReconciliation.discountPrecision', {
              decimals: result.rows.find((row) => row.discountDecimalPlaces != null)?.discountDecimalPlaces ?? 2,
            })}</Typography>
            <Typography variant="body2">{t('salesForceAssetReconciliation.discrepanciesOnly')}</Typography>
            <Typography variant="body2">{t('salesForceAssetReconciliation.usageIsLicenseLevel')}</Typography>
            <Typography variant="body2">{t('salesForceAssetReconciliation.dateSources')}</Typography>
            <Typography variant="body2">{t('salesForceAssetReconciliation.productCandidatesExplanation')}</Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>
      {sync.dialog}
    </Box>
  );
}
